import React from "react";
import { CheckCircle2, AlertTriangle, Info, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader, RequireModel } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import { Card, Badge } from "@/components/ui";

const SEV = {
  error: { icon: XCircle, tone: "danger", cls: "text-red-400" },
  warning: { icon: AlertTriangle, tone: "warning", cls: "text-amber-400" },
  info: { icon: Info, tone: "primary", cls: "text-primary" },
};

export default function CodeCheck() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const [wm] = useWorkingModel();

  const { data, loading, error } = useDeliverable(
    () => api.editorCodeCheck({ model: wm.model, wall_height_m: 3.2, design_code: wm.design_code }),
    [wm?.title, wm?.design_code],
    !!wm?.model
  );

  return (
    <RequireModel>
      {() => (
        <div className="mx-auto max-w-5xl px-4 py-6">
          <PageHeader
            eyebrow={isAr ? "فحص الكود" : "Code Check"}
            title={isAr ? "الامتثال للكود الإنشائي" : "Building-code compliance"}
            subtitle={isAr ? "فحص أبعاد الغرف والأبواب والدرج مقابل حدود SBC 201 / IRC." : "Room, door and stair dimensions checked against SBC 201 / IRC limits."}
            right={data && <Badge tone={data.passed ? "success" : "danger"}>{data.passed ? (isAr ? "مطابق" : "PASSED") : (isAr ? "توجد مخالفات" : "ISSUES")}</Badge>}
          />
          <DeliverableState loading={loading} error={error} isAr={isAr}>
            {data && (
              <>
                <div className="mb-4 grid grid-cols-3 gap-3">
                  {[["error", data.summary?.error], ["warning", data.summary?.warning], ["info", data.summary?.info]].map(([k, n]) => {
                    const S = SEV[k];
                    return (
                      <Card key={k} className="flex items-center gap-3 p-4">
                        <S.icon className={S.cls} size={22} />
                        <div>
                          <div className="text-2xl font-black">{n ?? 0}</div>
                          <div className="text-xs capitalize text-muted-foreground">{k}</div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
                <div className="space-y-2">
                  {(data.issues || []).length === 0 ? (
                    <Card className="flex items-center gap-2 text-emerald-400"><CheckCircle2 size={18} /> {isAr ? "كل الفحوص مطابقة للكود" : "All checks comply with the code"}</Card>
                  ) : (
                    (data.issues || []).map((it, i) => {
                      const S = SEV[it.severity] || SEV.info;
                      return (
                        <Card key={i} className="flex items-start gap-3 p-4">
                          <S.icon className={`${S.cls} mt-0.5 shrink-0`} size={18} />
                          <div className="flex-1">
                            <div className="text-sm font-bold">{it.label} · <span className="text-muted-foreground">{it.code_ref}</span></div>
                            <div className="text-xs text-muted-foreground">{isAr ? it.message_ar : it.message_en}</div>
                            <div className="mt-1 text-xs">{isAr ? "الحالي" : "current"}: <b>{it.current}</b> · {isAr ? "الموصى" : "recommended"}: <b className="text-emerald-400">{it.recommended}</b></div>
                          </div>
                        </Card>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </DeliverableState>
        </div>
      )}
    </RequireModel>
  );
}
